import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function Economics32() {

   const contentRef = useRef(null);
         const links = {
        prev: "/economics-31", 
        index: "/index-economics",                 
        next: "/economics-33", 
      };
        const titleText = "Machines and Labor";
  const questions = [
  {
    text: "1. What is the main reason people wrongly believe that machines cause unemployment?",
    options: [
      { value: "A", label: "A. Machines are too expensive to maintain" },
      { value: "B", label: "B. Machines are difficult to operate" },
      { value: "C", label: "C. Labor is mistaken for a consumer good" },
      { value: "D", label: "D. Governments replace workers intentionally" },
    ],
    correct: "C",
  },
  {
    text: "2. Which of the following best describes labor in economic terms?",
    options: [
      { value: "A", label: "A. A consumer product that becomes outdated" },
      { value: "B", label: "B. A social benefit provided by the government" },
      { value: "C", label: "C. A producer good used in the creation of other goods" },
      { value: "D", label: "D. An optional input in modern economies" },
    ],
    correct: "C",
  },
  {
    text: "3. What typically happens when machines are introduced into the workplace?",
    options: [
      { value: "A", label: "A. All workers are permanently laid off" },
      { value: "B", label: "B. Workers are replaced and never find work again" },
      { value: "C", label: "C. Labor shifts toward new roles and industries" },
      { value: "D", label: "D. Machines completely take over all economic activity" },
    ],
    correct: "C",
  },
  {
    text: "4. According to the lesson, what is the real threat to labor in the context of technological change?",
    options: [
      { value: "A", label: "A. The rising cost of machines" },
      { value: "B", label: "B. The reduction in global demand" },
      { value: "C", label: "C. The misunderstanding of economic progress" },
      { value: "D", label: "D. Poor factory lighting conditions" },
    ],
    correct: "C",
  },
];
// State to track selected answers for all questions
      const [answers, setAnswers] = useState({});
    
      // Function to update an answer
      const handleAnswerChange = (questionIndex, selectedValue) => {
        setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
      };
    
      // Compute score
      const score = questions.reduce((total, q, index) => {
        return total + (answers[index] === q.correct ? 1 : 0);
      }, 0);
    

  return (
    <div className="container">
       <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={32}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 32</b>! Today we will debunk the fear that machines
        and technology take away human jobs. A claim that has echoed through
        generations. It’s a claim so persistent that even today, in an era of
        remarkable innovation and interconnected global economies, one can hear
        this concern resurface in political speeches, labor union protests, and
        social media commentary. However, as students of Austrian Economics come
        to learn, this fear stems not from sound economic reasoning but from a
        misunderstanding of how labor, innovation, and market forces interact.
        The truth is that far from causing unemployment, the rise of machines
        has historically served to enhance human productivity, generate new
        opportunities, and raise the overall standard of living.
        <br />
        Humans have always been toolmakers. From the earliest days of history,
        people have used tools to amplify their capabilities through stone
        spears for hunting, wooden boats for fishing and travel, metal plows for
        agriculture. These inventions were not seen as threats but as extensions
        of human effort. The industrial revolution simply marked a more rapid
        phase of this process, introducing machines that could assist or even
        replace repetitive, dangerous, or physically demanding tasks. Rather
        than resist this shift, history shows that societies which embraced
        innovation flourished economically, while those that resisted it
        stagnated. It’s important to understand that machines do not remove the
        need for human contribution; they change the nature of that
        contribution.
        <br />
        Yet, resistance to change has been a recurring theme. Worker unions
        throughout history have often opposed innovation, mistakenly believing
        it to be a threat to their employment. This opposition is rarely based
        on a detailed analysis of economic forces. Instead, it tends to be
        driven by short-term thinking and a desire to preserve existing
        structures, even when they are inefficient. In many cases, these
        protests were conducted under slogans warning of 'machines replacing
        humans', but this narrative ignores the broader economic truth. When a
        machine replaces a job, it doesn’t erase human economic value; it
        reallocates it. Labor that is no longer required in one industry becomes
        available to other industries. And in practice, this transition has
        often led to higher wages, better working conditions, and entirely new
        sectors of employment.
        <br />
        <br />
        <b>Context Matters: Beyond Short-Term Statistics</b>
        <br />
        <br />
        One commonly used argument by technophobes is the citation of employment
        statistics immediately following the introduction of a particular
        technology. For example, one might point to a dip in demand for
        traditional painters after the adoption of spray painting machines, or
        the disappearance of horse carriage drivers after the arrival of
        automobiles. While these statistics may be factually correct in the
        short term, they lack economic relevance unless viewed in full context.
        Austrian economics teaches us that a proper analysis must include the
        long-term impact on productivity, consumer welfare, and capital
        reinvestment. Without this broader lens, any number can be used to
        justify fear, even if it leads to poor policy decisions or misinformed
        public opinion. History has consistently shown that those who focus
        solely on short-term numbers miss the larger, positive transformations
        that innovation unleashes.
        <br />
        Moreover, the arguments of those opposing labor-saving devices fall
        apart when scrutinized under the principle of revealed preferences.
        Every rational individual strives to complete a task in the most
        cost-efficient and time-saving manner. Even those who argue passionately
        against machines use them every day from typing on computers instead of
        typewriters to using washing machines instead of hand-washing clothes.
        If their logic were consistent, we would expect them to reject
        transportation vehicles in favor of carrying loads on foot or using
        typewriters instead of word processors. But they don’t, because in their
        personal lives, they choose what saves them time and effort. This
        contradiction reveals that the argument is not against machines per se,
        but against the temporary disruption machines may bring to certain
        employment patterns.
        <br />
        To better understand this idea, consider the example of a coat
        manufacturer. Suppose this entrepreneur purchases a machine that
        automates the production of coats, replacing the need for some human
        labor. At first glance, this may appear as a loss of jobs. But economic
        reasoning requires us to look beyond the surface. That machine did not
        appear out of thin air. It had to be designed, engineered, manufactured,
        transported, and maintained all activities that require human labor. In
        other words, the capital investment made by the coat manufacturer
        actually created new jobs in industries that support machinery
        production. These are jobs that did not exist before the innovation
        occurred.
        <br />
        Furthermore, by producing coats more efficiently, the manufacturer
        reduces costs, potentially lowers prices, and gains more revenue. That
        extra profit does not vanish it gets reinvested. The manufacturer may
        choose to expand operations, explore new product lines, or spend more on
        goods and services. In each case, money flows back into the economy and
        supports employment elsewhere. If other coat manufacturers adopt similar
        machinery, the machine-building industry will see an increase in demand.
        This creates a virtuous cycle: as more manufacturers improve
        productivity, the cost of goods drops, making products like coats more
        affordable to the general public. Consumers, in turn, have more
        disposable income to spend or invest, stimulating demand in other
        sectors. The net effect is not job destruction but job transformation
        and diversification. Over time, society benefits through higher
        standards of living, improved access to quality goods, and more dynamic
        economic activity.
        <br />
        <br />
        <b>Not Just About Efficiency: Machines Enrich Human Life</b>
        <br />
        <br />
        It’s also important to note that not all innovations aim to reduce
        labor. Some inventions serve to enhance the quality of human work or
        enable us to do things that were previously impossible. Plywood, for
        instance, doesn’t replace workers it allows them to build with more
        strength and precision. X-ray machines haven’t eliminated doctors;
        they’ve enabled early detection and better treatment. Airplanes haven’t
        put people out of jobs they’ve created entire industries, from tourism
        and logistics to aerospace engineering. These technologies represent
        extensions of human capability, not substitutes for it.
        <br />
        The idea that machines exist solely to boost production is also
        incorrect. Many machines have had profound humanitarian impacts. Air
        conditioners, for example, have drastically reduced deaths caused by
        extreme heat. Medical technologies have saved countless lives through
        early diagnosis and improved treatments. Innovations in safety
        equipment, nutrition, water purification, and emergency response systems
        have all contributed to making the modern world safer and healthier.
        Machines, in this sense, are not simply about doing more they’re about
        living better.
        <br />
        Still, critics of industrial progress often fall into the trap of
        short-term thinking. They focus on immediate job displacement and
        extrapolate those effects without accounting for how economies adapt.
        It’s easy to see that a factory replacing workers with machines might
        reduce headcount in that building. It’s harder to trace the ripple
        effect: how those machines are made, how they lead to cheaper goods, how
        consumer savings translate into demand elsewhere, and how industries
        evolve to absorb displaced workers. Austrian economists caution against
        judging economic shifts without understanding these complex dynamics.
        <br />
        <br />
        <b>Labor Is Not Obsolete: It Evolves with Progress</b>
        <br />
        <br />
        The Luddite movement in 18th-century Britain is a striking historical
        case. Textile workers, fearing the consequences of new weaving
        technologies, destroyed machinery and demanded a halt to innovation.
        Their fears, although understandable given their limited perspective,
        proved unfounded. Fast forward to the modern British economy: the
        average worker enjoys a higher standard of living, more diverse
        employment options, and safer working conditions than any Luddite ever
        imagined. The very technologies that were once condemned are now the
        foundation of prosperity. The same principle applies today. The
        industries we take for granted software, digital design, e-commerce,
        aerospace, pharmaceuticals all emerged from a foundation of continuous
        innovation.
        <br />
        The root of confusion lies in a fundamental economic misunderstanding:
        many assume that labor is a consumer good, the demand for which ends
        once a superior replacement is found. This logic holds for products like
        typewriters, which became obsolete with the arrival of personal
        computers. But labor is not like a typewriter, as it is a producer good,
        meaning its value lies in what it helps produce. If one type of labor
        becomes less necessary in a particular sector, that labor does not
        disappear, rather it is redirected. Someone who once worked in a factory
        may retrain and find employment in a service industry, a creative field,
        or a technology firm. Labor evolves as markets evolve.
        <br />
        In fact, one of the most remarkable features of modern economies is the
        explosion of specialized labor roles that didn’t exist just a few
        decades ago. Jobs like social media manager, app developer,
        cybersecurity analyst, drone operator, genetic counselor, and data
        scientist are all products of technological progress. Each one reflects
        the same pattern: machines handle repetitive, standardized tasks,
        freeing humans to explore more intellectually or emotionally engaging
        roles. With this shift comes not just employment but personal
        fulfillment and creativity.
        <br />
        The mistake made by those who fear machines is to treat the economy as a
        fixed pie, where more efficiency for one actor means less for another.
        Austrian Economics, in contrast, sees the economy as a dynamic and
        expanding system driven by human action, discovery, and coordination
        through prices and voluntary exchange. When one door closes due to
        innovation, many others open. But only if people are free to respond to
        market signals, pursue new ventures, and adapt to changing
        circumstances.
        <br />
        The idea that machines destroy human jobs is not supported by historical
        evidence, logical consistency, or sound economic theory. Innovation does
        displace certain tasks, but it simultaneously creates new roles,
        industries, and opportunities. Technological progress increases
        productivity, reduces costs, and enhances the quality of life. When we
        look at labor as a flexible and valuable input into the production
        process rather than a rigid role to be protected, we begin to understand
        how societies grow and prosper. Far from being a threat to humanity,
        machines are among our greatest allies in the pursuit of abundance,
        creativity, and freedom.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today’s lesson dismantled the common myth that machines inevitably lead
        to mass unemployment. We examined how labor, unlike consumer goods, is a
        producer good its value lies in its contribution to further production.
        Through historical examples and economic reasoning, we saw that while
        machines may displace certain types of work, they simultaneously create
        new roles, industries, and opportunities. The fear that machines
        “replace” humans overlooks the dynamic nature of labor markets, where
        skills shift and evolve rather than disappear. Rather than destroying
        work, machines have historically enhanced human productivity, elevated
        living standards, and liberated workers from monotonous or hazardous
        tasks. The real threat to labor isn’t machinery, but misunderstanding
        the nature of economic progress.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />On Day 33, we will explore the concept of forced division of labor and its impact on economic freedom and productivity. The lesson will examine how top-down control of labor assignments, often implemented through central planning or coercive state policies, leads to inefficiencies and stifles individual potential. We will also discuss why voluntary labor specialization, guided by personal choice and market signals, results in a more dynamic and prosperous society. By understanding the consequences of imposed labor structures, learners will gain deeper insight into the importance of economic liberty and the value of allowing individuals to choose their own roles in the economy.
      </p>
      <h2>Test your understanding</h2>
       {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics32;
